import React from "react";
import PropTypes from "prop-types";
import cx from "clsx";
import { useAppTranslation } from "../../../../i18n/useAppTranslation";
import { AlertBox } from "../../../../shared/AlertBox/AlertBox";
import {
  OTP_CHANNELS,
  OTP_FAILOVER_STATUSES,
} from "../../../../utilities/otpFailover";
import classes from "./styles.module.scss";

const RING_RADIUS = 54;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * Shows which channel the 2FA code is on and the countdown before the automatic voice call.
 */
export function OtpFailoverPanel({ state, secondsLeft }) {
  const { tAuth } = useAppTranslation.Auth({
    keyPrefix: "twoFactor.otpFailover",
  });

  if (!state) {
    return null;
  }

  const isCall = state.activeChannel === OTP_CHANNELS.call;

  return (
    <div className={classes.root}>
      <span
        className={cx(classes.badge, {
          [classes.badgeSms]: !isCall,
          [classes.badgeCall]: isCall,
        })}
      >
        {tAuth(`channel.${state.activeChannel}`)}
      </span>

      {state.status === OTP_FAILOVER_STATUSES.countingDown && (
        <>
          <div className={classes.ring} role="timer">
            <svg viewBox="0 0 120 120" aria-hidden>
              <circle
                className={classes.ringTrack}
                cx="60"
                cy="60"
                r={RING_RADIUS}
              />
              <circle
                className={classes.ringProgress}
                cx="60"
                cy="60"
                r={RING_RADIUS}
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={
                  RING_CIRCUMFERENCE * (1 - secondsLeft / state.totalSeconds)
                }
              />
            </svg>
            <span className={classes.ringSeconds}>{secondsLeft}</span>
          </div>
          <div className={classes.hint}>
            {tAuth("countdown", { seconds: secondsLeft })}
          </div>
        </>
      )}

      {state.status === OTP_FAILOVER_STATUSES.failingOver && (
        <AlertBox size="small" variant="success" className={classes.notice}>
          {tAuth("failingOver")}
        </AlertBox>
      )}

      {state.status === OTP_FAILOVER_STATUSES.settled && state.usedFailover && (
        <AlertBox size="small" variant="success" className={classes.notice}>
          {tAuth(state.autoFailover ? "autoFailover" : "failedOver")}
        </AlertBox>
      )}

      {state.status === OTP_FAILOVER_STATUSES.error && (
        <AlertBox size="small" variant="warn" className={classes.notice}>
          {tAuth([`error.${state.error}`, "error.fallback"])}
        </AlertBox>
      )}
    </div>
  );
}

OtpFailoverPanel.propTypes = {
  state: PropTypes.object,
  secondsLeft: PropTypes.number.isRequired,
};
